/**
 * Build a Paladin-branded Instagram-Stories-sized shareable PR card
 * as a PNG Blob using the Canvas API. No dependencies.
 *
 * Returns: Promise<Blob> (image/png, 1080x1920)
 */

const W = 1080;
const H = 1920;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * data: {
 *   athleteName: string,
 *   workoutTitle: string,
 *   volume: number,         // lbs total
 *   sets: number,
 *   duration: number,       // minutes
 *   pr?: { exercise, weight, previous } | null,
 *   funLine?: string        // e.g. "3.2 alpacas 🦙"
 * }
 */
export async function buildShareImage(data) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient (Paladin dark navy → slightly warmer navy)
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0B0F1A");
  bg.addColorStop(0.5, "#0F172A");
  bg.addColorStop(1, "#1E293B");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle radial glow (blue)
  const glow = ctx.createRadialGradient(W / 2, H * 0.25, 10, W / 2, H * 0.25, 900);
  glow.addColorStop(0, "rgba(59, 130, 246, 0.35)");
  glow.addColorStop(1, "rgba(59, 130, 246, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Paladin wordmark at top
  ctx.fillStyle = "#3B82F6";
  ctx.font = "900 64px system-ui, -apple-system, Arial";
  ctx.textAlign = "center";
  ctx.fillText("PALADIN", W / 2, 180);
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "600 22px system-ui, -apple-system, Arial";
  ctx.fillText("HEALTH · HUMAN PERFORMANCE", W / 2, 220);

  const hasPR = !!data.pr;

  // Main card (centered)
  const cardX = 80;
  const cardY = 340;
  const cardW = W - 160;
  const cardH = hasPR ? 1000 : 860;
  ctx.fillStyle = "rgba(20, 27, 45, 0.85)";
  roundRect(ctx, cardX, cardY, cardW, cardH, 40);
  ctx.fill();
  ctx.strokeStyle = "rgba(59, 130, 246, 0.3)";
  ctx.lineWidth = 3;
  roundRect(ctx, cardX, cardY, cardW, cardH, 40);
  ctx.stroke();

  let y = cardY + 110;

  // Headline
  if (hasPR) {
    ctx.fillStyle = "#F59E0B";
    ctx.font = "700 28px system-ui, -apple-system, Arial";
    ctx.textAlign = "center";
    ctx.fillText("NEW PERSONAL RECORD", W / 2, y);
    y += 50;

    // PR exercise name (wrap)
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 52px system-ui, -apple-system, Arial";
    const exLines = wrapText(ctx, data.pr.exercise, cardW - 100);
    exLines.forEach((line) => {
      ctx.fillText(line, W / 2, y);
      y += 64;
    });
    y += 30;

    // PR weight, BIG
    ctx.fillStyle = "#3B82F6";
    ctx.font = "900 200px system-ui, -apple-system, Arial";
    ctx.fillText(`${Math.round(data.pr.weight)}`, W / 2, y + 130);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 48px system-ui, -apple-system, Arial";
    ctx.fillText("lbs", W / 2, y + 200);
    y += 280;

    // +delta
    if (data.pr.previous > 0) {
      const delta = Math.round(data.pr.weight - data.pr.previous);
      ctx.fillStyle = "#10B981";
      ctx.font = "800 40px system-ui, -apple-system, Arial";
      ctx.fillText(`+${delta} lbs over previous best`, W / 2, y);
      y += 80;
    }
  } else {
    // No PR — show "Workout Complete"
    ctx.fillStyle = "#10B981";
    ctx.font = "700 28px system-ui, -apple-system, Arial";
    ctx.textAlign = "center";
    ctx.fillText("WORKOUT COMPLETE", W / 2, y);
    y += 70;

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 56px system-ui, -apple-system, Arial";
    const tLines = wrapText(ctx, data.workoutTitle || "Great Session", cardW - 120);
    tLines.forEach((line) => {
      ctx.fillText(line, W / 2, y);
      y += 72;
    });
    y += 40;
  }

  // Stat row
  const statY = cardY + cardH - 260;
  const statsData = [
    { v: `${Math.round(data.volume || 0).toLocaleString()}`, l: "LBS LIFTED" },
    { v: `${data.sets || 0}`, l: "SETS" },
    { v: `${data.duration || 0}m`, l: "DURATION" },
  ];
  const colW = cardW / statsData.length;
  statsData.forEach((s, i) => {
    const cx = cardX + colW * i + colW / 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 56px system-ui, -apple-system, Arial";
    ctx.fillText(s.v, cx, statY + 60);
    ctx.fillStyle = "#6B7280";
    ctx.font = "700 22px system-ui, -apple-system, Arial";
    ctx.fillText(s.l, cx, statY + 100);
  });

  // Fun line (e.g., "3.2 alpacas worth of weight 🦙")
  if (data.funLine) {
    const fy = cardY + cardH - 80;
    ctx.fillStyle = "rgba(245, 158, 11, 0.12)";
    const funBoxW = cardW - 80;
    roundRect(ctx, cardX + 40, fy - 45, funBoxW, 75, 38);
    ctx.fill();
    ctx.fillStyle = "#F59E0B";
    ctx.font = "700 30px system-ui, -apple-system, Arial";
    ctx.textAlign = "center";
    ctx.fillText(data.funLine, W / 2, fy + 6);
  }

  // Footer: athlete + app tag
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 38px system-ui, -apple-system, Arial";
  ctx.textAlign = "center";
  ctx.fillText(data.athleteName || "", W / 2, H - 200);

  ctx.fillStyle = "#9CA3AF";
  ctx.font = "600 26px system-ui, -apple-system, Arial";
  ctx.fillText("paladinhhp.com", W / 2, H - 150);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.95);
  });
}
